import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import { checkStatus, parseJSON, handleError } from '../../../shared/api/core';
import { Toast, ToastError, ToastSuccess } from '../../../shared/components/Notifier';
import { getProductsByPage } from '../../../products/api/Api';
import { Product } from '../../../products/models';
import {
    createExpiringStockPromoTemplate,
    previewExpiringStockPromoTemplate,
    updateExpiringStockPromoTemplate,
} from '../../api/Api';
import BenefitConfigForm from '../../components/BenefitConfigForm';
import BuyXGetYForm from '../../components/BuyXGetYForm';
import TriggerConfigForm from '../../components/TriggerConfigForm';
import {
    AmountTriggerFormState,
    BENEFIT_TYPE_LABELS,
    buildBenefitConfig,
    buildBuyXGetYBenefitConfig,
    buildBuyXGetYNameTemplate,
    buildBuyXGetYTriggerConfig,
    buildTriggerConfig,
    BuyXGetYFormState,
    CommonBenefitFormState,
    DEFAULT_BUY_X_GET_Y,
    DEFAULT_COMMON_BENEFIT,
    DEFAULT_EXTENDED_BENEFIT,
    DEFAULT_GRADUATED_DISCOUNT,
    DEFAULT_PROMO_NAME_TEMPLATE,
    DEFAULT_REDUCE_PRODUCT_AMOUNT,
    DEFAULT_TRIGGER,
    detectPromoPattern,
    ExtendedBenefitFormState,
    extendedFromBuyXGetY,
    GraduatedDiscountFormState,
    parseBuyXGetYFromConfigs,
    parseCommonBenefit,
    parseExtendedBenefit,
    parseGraduatedDiscount,
    parseReduceProductAmount,
    parseTriggerConfig,
    PROMO_PATTERN_LABELS,
    PROMO_TYPE_LABELS,
    PromoPattern,
    ReduceProductAmountFormState,
    triggerFromBuyXGetY,
} from '../../lib/koronaPromotionConfig';
import {
    ExpiringStockBenefitType,
    ExpiringStockPromoTemplate,
    ExpiringStockPromoType,
} from '../../models';

const PROMO_TYPE_OPTIONS: ExpiringStockPromoType[] = ['IMMEDIATE_DISCOUNT', 'COUPON_DISCOUNT'];
const STANDARD_BENEFIT_TYPE_OPTIONS: ExpiringStockBenefitType[] = [
    'COMMON',
    'REDUCE_PRODUCT_AMOUNT',
    'GRADUATED_DISCOUNT',
];
const PATTERN_OPTIONS: PromoPattern[] = ['STANDARD', 'BUY_X_GET_Y', 'EXTENDED_ADVANCED'];

interface Props {
    open: boolean;
    mode: 'CREATE' | 'EDIT' | 'PREVIEW';
    template: ExpiringStockPromoTemplate | null;
    businessId: number | string;
    onClose: () => void;
    onSaved: () => void;
    showToast: (toast: Toast) => void;
}

const highlightJson = (code: string) => Prism.highlight(code, Prism.languages.json, 'json');

function loadBenefitFormState(
    benefitType: ExpiringStockBenefitType,
    config: Record<string, unknown>
): {
    common: CommonBenefitFormState;
    reduceProductAmount: ReduceProductAmountFormState;
    graduated: GraduatedDiscountFormState;
    extended: ExtendedBenefitFormState;
} {
    return {
        common: parseCommonBenefit(benefitType === 'COMMON' ? config : {}),
        reduceProductAmount: parseReduceProductAmount(benefitType === 'REDUCE_PRODUCT_AMOUNT' ? config : {}),
        graduated: parseGraduatedDiscount(benefitType === 'GRADUATED_DISCOUNT' ? config : {}),
        extended: parseExtendedBenefit(benefitType === 'EXTENDED' ? config : {}),
    };
}

const PromoTemplateFormDialog: React.FC<Props> = ({ open, mode, template, businessId, onClose, onSaved, showToast }) => {
    const isPreview = mode === 'PREVIEW';

    const [name, setName] = useState(template?.name || '');
    const [promoType, setPromoType] = useState<ExpiringStockPromoType>(template?.promo_type || 'IMMEDIATE_DISCOUNT');
    const [pattern, setPattern] = useState<PromoPattern>('STANDARD');
    const [benefitType, setBenefitType] = useState<ExpiringStockBenefitType>(template?.benefit_type || 'COMMON');
    const [commonBenefit, setCommonBenefit] = useState<CommonBenefitFormState>(DEFAULT_COMMON_BENEFIT);
    const [reduceProductAmount, setReduceProductAmount] = useState<ReduceProductAmountFormState>(
        DEFAULT_REDUCE_PRODUCT_AMOUNT
    );
    const [graduatedDiscount, setGraduatedDiscount] = useState<GraduatedDiscountFormState>(DEFAULT_GRADUATED_DISCOUNT);
    const [extendedBenefit, setExtendedBenefit] = useState<ExtendedBenefitFormState>(DEFAULT_EXTENDED_BENEFIT);
    const [buyXGetY, setBuyXGetY] = useState<BuyXGetYFormState>(DEFAULT_BUY_X_GET_Y);
    const [triggerForm, setTriggerForm] = useState<AmountTriggerFormState>(DEFAULT_TRIGGER);
    const [promoNameTemplate, setPromoNameTemplate] = useState(
        template?.promo_name_template || DEFAULT_PROMO_NAME_TEMPLATE
    );
    const [receiptInfoTemplate, setReceiptInfoTemplate] = useState(template?.receipt_info_text_template || '');
    const [isDefault, setIsDefault] = useState(!!template?.is_default);
    const [showAdvancedJson, setShowAdvancedJson] = useState(false);
    const [advancedBenefitJson, setAdvancedBenefitJson] = useState('{}');
    const [advancedTriggerJson, setAdvancedTriggerJson] = useState('{}');
    const [isSaving, setSaving] = useState(false);

    const [previewProductOptions, setPreviewProductOptions] = useState<Product[]>([]);
    const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
    const [previewProductSearchLoading, setPreviewProductSearchLoading] = useState(false);
    const [previewExpiryDate, setPreviewExpiryDate] = useState('');
    const [previewResult, setPreviewResult] = useState<{ promo_name: string; receipt_info_text: string | null } | null>(
        null
    );
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewProductSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previewProductSearchRequestIdRef = useRef(0);

    const syncAdvancedJsonFromForms = (
        nextPattern = pattern,
        nextBenefitType = benefitType,
        nextCommon = commonBenefit,
        nextReduce = reduceProductAmount,
        nextGraduated = graduatedDiscount,
        nextExtended = extendedBenefit,
        nextBuyXGetY = buyXGetY,
        nextTrigger = triggerForm
    ) => {
        let benefitConfig: Record<string, unknown>;
        let triggerConfig: Record<string, unknown>;
        if (nextPattern === 'BUY_X_GET_Y') {
            benefitConfig = buildBuyXGetYBenefitConfig(nextBuyXGetY);
            triggerConfig = buildBuyXGetYTriggerConfig(nextBuyXGetY);
        } else {
            benefitConfig = buildBenefitConfig(
                nextBenefitType,
                nextCommon,
                nextReduce,
                nextGraduated,
                nextExtended
            );
            triggerConfig = buildTriggerConfig(nextTrigger);
        }
        setAdvancedBenefitJson(JSON.stringify(benefitConfig, null, 2));
        setAdvancedTriggerJson(JSON.stringify(triggerConfig, null, 2));
    };

    useEffect(() => {
        if (isPreview) return;
        const benefitConfig = (template?.benefit_config || {}) as Record<string, unknown>;
        const triggerConfig = (template?.trigger_config || {}) as Record<string, unknown>;
        const nextBenefitType = template?.benefit_type || 'COMMON';
        const nextPattern = template
            ? detectPromoPattern(nextBenefitType, benefitConfig, triggerConfig)
            : 'STANDARD';
        const forms = loadBenefitFormState(nextBenefitType, benefitConfig);
        const nextTrigger = parseTriggerConfig(triggerConfig);
        const parsedBuyXGetY = parseBuyXGetYFromConfigs(benefitConfig, triggerConfig);

        setName(template?.name || '');
        setPromoType(template?.promo_type || 'IMMEDIATE_DISCOUNT');
        setPattern(nextPattern);
        setBenefitType(nextBenefitType);
        setCommonBenefit(forms.common);
        setReduceProductAmount(forms.reduceProductAmount);
        setGraduatedDiscount(forms.graduated);
        setExtendedBenefit(forms.extended);
        setBuyXGetY(parsedBuyXGetY || DEFAULT_BUY_X_GET_Y);
        setTriggerForm(nextTrigger);
        setPromoNameTemplate(template?.promo_name_template || DEFAULT_PROMO_NAME_TEMPLATE);
        setReceiptInfoTemplate(template?.receipt_info_text_template || '');
        setIsDefault(!!template?.is_default);
        setShowAdvancedJson(false);

        if (nextPattern === 'BUY_X_GET_Y' && parsedBuyXGetY) {
            setAdvancedBenefitJson(JSON.stringify(buildBuyXGetYBenefitConfig(parsedBuyXGetY), null, 2));
            setAdvancedTriggerJson(JSON.stringify(buildBuyXGetYTriggerConfig(parsedBuyXGetY), null, 2));
        } else {
            const builtBenefit = buildBenefitConfig(
                nextBenefitType,
                forms.common,
                forms.reduceProductAmount,
                forms.graduated,
                forms.extended
            );
            const builtTrigger = buildTriggerConfig(nextTrigger);
            setAdvancedBenefitJson(JSON.stringify(builtBenefit, null, 2));
            setAdvancedTriggerJson(JSON.stringify(builtTrigger, null, 2));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, isPreview]);

    const handlePatternChange = (nextPattern: PromoPattern) => {
        setPattern(nextPattern);
        if (nextPattern === 'BUY_X_GET_Y') {
            setBenefitType('EXTENDED');
            const nextBuy = DEFAULT_BUY_X_GET_Y;
            setBuyXGetY(nextBuy);
            setExtendedBenefit(extendedFromBuyXGetY(nextBuy));
            setTriggerForm(triggerFromBuyXGetY(nextBuy));
            setPromoNameTemplate(buildBuyXGetYNameTemplate(nextBuy));
            syncAdvancedJsonFromForms(
                nextPattern,
                'EXTENDED',
                commonBenefit,
                reduceProductAmount,
                graduatedDiscount,
                extendedFromBuyXGetY(nextBuy),
                nextBuy,
                triggerFromBuyXGetY(nextBuy)
            );
            return;
        }
        if (nextPattern === 'EXTENDED_ADVANCED') {
            setBenefitType('EXTENDED');
            setExtendedBenefit(DEFAULT_EXTENDED_BENEFIT);
            setTriggerForm({
                ...DEFAULT_TRIGGER,
                triggerType: 'productAndReceiptAmount',
                value: '1',
            });
            syncAdvancedJsonFromForms(
                nextPattern,
                'EXTENDED',
                commonBenefit,
                reduceProductAmount,
                graduatedDiscount,
                DEFAULT_EXTENDED_BENEFIT,
                buyXGetY,
                { ...DEFAULT_TRIGGER, triggerType: 'productAndReceiptAmount', value: '1' }
            );
            return;
        }
        setBenefitType('COMMON');
        const defaults = loadBenefitFormState('COMMON', {});
        setCommonBenefit(defaults.common);
        setReduceProductAmount(defaults.reduceProductAmount);
        setGraduatedDiscount(defaults.graduated);
        setTriggerForm(DEFAULT_TRIGGER);
        setPromoNameTemplate(DEFAULT_PROMO_NAME_TEMPLATE);
        syncAdvancedJsonFromForms(
            nextPattern,
            'COMMON',
            defaults.common,
            defaults.reduceProductAmount,
            defaults.graduated,
            defaults.extended,
            buyXGetY,
            DEFAULT_TRIGGER
        );
    };

    const handleBenefitTypeChange = (nextType: ExpiringStockBenefitType) => {
        setBenefitType(nextType);
        const defaults = loadBenefitFormState(nextType, {});
        setCommonBenefit(defaults.common);
        setReduceProductAmount(defaults.reduceProductAmount);
        setGraduatedDiscount(defaults.graduated);
        setExtendedBenefit(defaults.extended);
        syncAdvancedJsonFromForms(
            pattern,
            nextType,
            defaults.common,
            defaults.reduceProductAmount,
            defaults.graduated,
            defaults.extended,
            buyXGetY,
            triggerForm
        );
    };

    const handleBuyXGetYChange = (next: BuyXGetYFormState) => {
        setBuyXGetY(next);
        setExtendedBenefit(extendedFromBuyXGetY(next));
        const nextTrigger = triggerFromBuyXGetY(next);
        setTriggerForm(nextTrigger);
        setPromoNameTemplate(buildBuyXGetYNameTemplate(next));
        if (!showAdvancedJson) {
            syncAdvancedJsonFromForms(
                pattern,
                'EXTENDED',
                commonBenefit,
                reduceProductAmount,
                graduatedDiscount,
                extendedFromBuyXGetY(next),
                next,
                nextTrigger
            );
        }
    };

    const handleBenefitFormChange = (
        patch: Partial<{
            common: CommonBenefitFormState;
            reduceProductAmount: ReduceProductAmountFormState;
            graduated: GraduatedDiscountFormState;
            extended: ExtendedBenefitFormState;
        }>
    ) => {
        const nextCommon = patch.common ?? commonBenefit;
        const nextReduce = patch.reduceProductAmount ?? reduceProductAmount;
        const nextGraduated = patch.graduated ?? graduatedDiscount;
        const nextExtended = patch.extended ?? extendedBenefit;
        if (patch.common) setCommonBenefit(patch.common);
        if (patch.reduceProductAmount) setReduceProductAmount(patch.reduceProductAmount);
        if (patch.graduated) setGraduatedDiscount(patch.graduated);
        if (patch.extended) setExtendedBenefit(patch.extended);
        if (!showAdvancedJson) {
            syncAdvancedJsonFromForms(
                pattern,
                benefitType,
                nextCommon,
                nextReduce,
                nextGraduated,
                nextExtended,
                buyXGetY,
                triggerForm
            );
        }
    };

    const handleTriggerFormChange = (nextTrigger: AmountTriggerFormState) => {
        setTriggerForm(nextTrigger);
        if (!showAdvancedJson) {
            syncAdvancedJsonFromForms(
                pattern,
                benefitType,
                commonBenefit,
                reduceProductAmount,
                graduatedDiscount,
                extendedBenefit,
                buyXGetY,
                nextTrigger
            );
        }
    };

    useEffect(() => {
        return () => {
            if (previewProductSearchDebounceRef.current) {
                clearTimeout(previewProductSearchDebounceRef.current);
            }
            previewProductSearchRequestIdRef.current += 1;
        };
    }, []);

    const searchPreviewProducts = (term: string) => {
        if (previewProductSearchDebounceRef.current) {
            clearTimeout(previewProductSearchDebounceRef.current);
            previewProductSearchDebounceRef.current = null;
        }

        const trimmed = term.trim();
        if (!businessId || !trimmed) {
            previewProductSearchRequestIdRef.current += 1;
            setPreviewProductOptions([]);
            setPreviewProductSearchLoading(false);
            return;
        }

        previewProductSearchDebounceRef.current = setTimeout(() => {
            const requestId = ++previewProductSearchRequestIdRef.current;
            setPreviewProductSearchLoading(true);
            getProductsByPage(1, 20, trimmed, businessId)
                .catch(handleError)
                .then(checkStatus)
                .then(parseJSON)
                .then((data: any) => {
                    if (requestId === previewProductSearchRequestIdRef.current) {
                        setPreviewProductOptions(data?.results || []);
                    }
                })
                .finally(() => {
                    if (requestId === previewProductSearchRequestIdRef.current) {
                        setPreviewProductSearchLoading(false);
                    }
                });
        }, 400);
    };

    const handleRunPreview = () => {
        if (!template || !previewProduct) return;
        setPreviewLoading(true);
        previewExpiringStockPromoTemplate(template.id, {
            product_id: previewProduct.uuid,
            product_expiry_date: previewExpiryDate || null,
        })
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => setPreviewResult(data))
            .catch(() => showToast({ message: 'Failed to generate preview.', options: ToastError }))
            .finally(() => setPreviewLoading(false));
    };

    const resolveConfigsForSave = (): {
        benefitConfig: Record<string, unknown>;
        triggerConfig: Record<string, unknown>;
        resolvedBenefitType: ExpiringStockBenefitType;
    } | null => {
        if (showAdvancedJson) {
            try {
                const triggerConfig = {
                    ...(advancedTriggerJson ? JSON.parse(advancedTriggerJson) : {}),
                    attachExternalCoupon: true,
                };
                return {
                    benefitConfig: advancedBenefitJson ? JSON.parse(advancedBenefitJson) : {},
                    triggerConfig,
                    resolvedBenefitType: pattern === 'STANDARD' ? benefitType : 'EXTENDED',
                };
            } catch (e) {
                showToast({ message: 'Benefit/Trigger config must be valid JSON.', options: ToastError });
                return null;
            }
        }
        if (pattern === 'BUY_X_GET_Y') {
            return {
                benefitConfig: buildBuyXGetYBenefitConfig(buyXGetY),
                triggerConfig: buildBuyXGetYTriggerConfig(buyXGetY),
                resolvedBenefitType: 'EXTENDED',
            };
        }
        return {
            benefitConfig: buildBenefitConfig(
                benefitType,
                commonBenefit,
                reduceProductAmount,
                graduatedDiscount,
                extendedBenefit
            ),
            triggerConfig: buildTriggerConfig(triggerForm),
            resolvedBenefitType: benefitType,
        };
    };

    const handleSave = () => {
        const configs = resolveConfigsForSave();
        if (!configs) return;

        const payload = {
            business: Number(businessId),
            name,
            promo_type: promoType,
            benefit_type: configs.resolvedBenefitType,
            benefit_config: configs.benefitConfig,
            trigger_config: configs.triggerConfig,
            promo_name_template: promoNameTemplate,
            receipt_info_text_template: receiptInfoTemplate || null,
            is_default: isDefault,
        };
        setSaving(true);
        const request =
            mode === 'EDIT' && template
                ? updateExpiringStockPromoTemplate(template.id, payload)
                : createExpiringStockPromoTemplate(payload);
        request
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                showToast({
                    message: `Promo template ${mode === 'EDIT' ? 'updated' : 'created'} successfully.`,
                    options: ToastSuccess,
                });
                onSaved();
            })
            .catch((error: any) => {
                const message =
                    error?.response?.data?.error || `Failed to ${mode === 'EDIT' ? 'update' : 'create'} promo template.`;
                showToast({ message, options: ToastError });
            })
            .finally(() => setSaving(false));
    };

    const isValid = !!name && !!promoNameTemplate;

    if (isPreview) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Preview - {template?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Select a sample product to render the promo name using this template's Django-style
                        expression, evaluated against the tracker/product/business/location context.
                    </Typography>
                    <Autocomplete<Product, false, false, false>
                        options={previewProductOptions}
                        value={previewProduct}
                        getOptionLabel={(option) => `${option.name} (${option.number})`}
                        loading={previewProductSearchLoading}
                        filterOptions={(options) => options}
                        onChange={(_, value) => setPreviewProduct(value)}
                        onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                                searchPreviewProducts(value);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Sample Product" variant="outlined" margin="normal" fullWidth />
                        )}
                    />
                    <TextField
                        label="Sample Expiry Date (optional)"
                        type="date"
                        value={previewExpiryDate}
                        onChange={(e) => setPreviewExpiryDate(e.target.value)}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleRunPreview}
                        disabled={!previewProduct || previewLoading}
                        style={{ marginTop: 8 }}
                    >
                        {previewLoading ? 'Generating...' : 'Generate Preview'}
                    </Button>
                    {previewResult && (
                        <div style={{ marginTop: 16 }}>
                            <Typography variant="subtitle2">Promo Name</Typography>
                            <Typography variant="body1" gutterBottom>
                                {previewResult.promo_name}
                            </Typography>
                            {previewResult.receipt_info_text && (
                                <>
                                    <Typography variant="subtitle2">Receipt Info Text</Typography>
                                    <Typography variant="body1">{previewResult.receipt_info_text}</Typography>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{mode === 'EDIT' ? `Edit Template - ${template?.name}` : 'Add Promo Template'}</DialogTitle>
            <DialogContent>
                <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
                <FormControl variant="outlined" fullWidth margin="normal">
                    <InputLabel id="promo-type-label">Promo Type</InputLabel>
                    <Select
                        labelId="promo-type-label"
                        label="Promo Type"
                        value={promoType}
                        onChange={(e) => setPromoType(e.target.value as ExpiringStockPromoType)}
                    >
                        {PROMO_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {PROMO_TYPE_LABELS[opt]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" fullWidth margin="normal">
                    <InputLabel id="promo-pattern-label">Promo Pattern</InputLabel>
                    <Select
                        labelId="promo-pattern-label"
                        label="Promo Pattern"
                        value={pattern}
                        onChange={(e) => handlePatternChange(e.target.value as PromoPattern)}
                    >
                        {PATTERN_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {PROMO_PATTERN_LABELS[opt]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {pattern === 'STANDARD' && (
                    <FormControl variant="outlined" fullWidth margin="normal">
                        <InputLabel id="benefit-type-label">Benefit Type</InputLabel>
                        <Select
                            labelId="benefit-type-label"
                            label="Benefit Type"
                            value={benefitType === 'EXTENDED' ? 'COMMON' : benefitType}
                            onChange={(e) => handleBenefitTypeChange(e.target.value as ExpiringStockBenefitType)}
                        >
                            {STANDARD_BENEFIT_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {BENEFIT_TYPE_LABELS[opt]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {pattern === 'BUY_X_GET_Y' && (
                    <BuyXGetYForm form={buyXGetY} onChange={handleBuyXGetYChange} />
                )}

                {pattern === 'STANDARD' && (
                    <BenefitConfigForm
                        benefitType={benefitType === 'EXTENDED' ? 'COMMON' : benefitType}
                        common={commonBenefit}
                        reduceProductAmount={reduceProductAmount}
                        graduated={graduatedDiscount}
                        extended={extendedBenefit}
                        onCommonChange={(state) => handleBenefitFormChange({ common: state })}
                        onReduceProductAmountChange={(state) =>
                            handleBenefitFormChange({ reduceProductAmount: state })
                        }
                        onGraduatedChange={(state) => handleBenefitFormChange({ graduated: state })}
                        onExtendedChange={(state) => handleBenefitFormChange({ extended: state })}
                    />
                )}

                {pattern === 'EXTENDED_ADVANCED' && (
                    <BenefitConfigForm
                        benefitType="EXTENDED"
                        common={commonBenefit}
                        reduceProductAmount={reduceProductAmount}
                        graduated={graduatedDiscount}
                        extended={extendedBenefit}
                        onCommonChange={(state) => handleBenefitFormChange({ common: state })}
                        onReduceProductAmountChange={(state) =>
                            handleBenefitFormChange({ reduceProductAmount: state })
                        }
                        onGraduatedChange={(state) => handleBenefitFormChange({ graduated: state })}
                        onExtendedChange={(state) => handleBenefitFormChange({ extended: state })}
                    />
                )}

                {pattern !== 'BUY_X_GET_Y' && (
                    <TriggerConfigForm
                        form={triggerForm}
                        onChange={handleTriggerFormChange}
                        lockCompoundProductTrigger={false}
                    />
                )}
                {pattern === 'BUY_X_GET_Y' && (
                    <TriggerConfigForm
                        form={triggerForm}
                        onChange={handleTriggerFormChange}
                        lockCompoundProductTrigger
                    />
                )}

                <TextField
                    label="Promo Name Template"
                    value={promoNameTemplate}
                    onChange={(e) => setPromoNameTemplate(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={2}
                    helperText="Django template string. Available: {{ product.name }}, {{ discount_percent }}, {{ reduction_value }}, {{ tracker.product_expiry_date }}"
                />
                <TextField
                    label="Receipt Info Text Template (optional)"
                    value={receiptInfoTemplate || ''}
                    onChange={(e) => setReceiptInfoTemplate(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={2}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showAdvancedJson}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                setShowAdvancedJson(enabled);
                                if (enabled) {
                                    syncAdvancedJsonFromForms();
                                }
                            }}
                        />
                    }
                    label="Show advanced JSON editor"
                    style={{ marginTop: 8 }}
                />
                <Collapse in={showAdvancedJson}>
                    <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                        Benefit Config (JSON)
                    </Typography>
                    <div style={{ border: '1px solid #ccc', borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                        <Editor
                            value={advancedBenefitJson}
                            onValueChange={setAdvancedBenefitJson}
                            highlight={highlightJson}
                            padding={10}
                            style={{ fontFamily: 'monospace', fontSize: 13, minHeight: 100 }}
                        />
                    </div>
                    <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                        Trigger Config (JSON)
                    </Typography>
                    <div style={{ border: '1px solid #ccc', borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                        <Editor
                            value={advancedTriggerJson}
                            onValueChange={setAdvancedTriggerJson}
                            highlight={highlightJson}
                            padding={10}
                            style={{ fontFamily: 'monospace', fontSize: 13, minHeight: 100 }}
                        />
                    </div>
                </Collapse>

                <FormControlLabel
                    control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                    label="Default template for this business"
                    style={{ marginTop: 8 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={!isValid || isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PromoTemplateFormDialog;
